import { c as _c } from "react/compiler-runtime";
import type { AccommodationRoomSegment, AccommodationRoomSegmentBed } from '@data-access/graphql/schema.types';
import { Bed, ChalkboardTeacher, Couch, Shower } from '@phosphor-icons/react';
import { formatGermanNumber } from '@utils/utils-format';
import { groupBy } from 'es-toolkit';
import { Fragment, type PropsWithChildren } from 'react';
const SEGMENT_ICONS = {
  bedroom: Bed,
  livingRoom: Couch,
  roomSize: ChalkboardTeacher,
  bathroom: Shower
} as const;
type RoomType = keyof typeof SEGMENT_ICONS;
type AccommodationRoomSegmentsProps = {
  segments: AccommodationRoomSegment[];
  size?: number | null;
  bathrooms?: number | null;
};
export function AccommodationRoomSegments(t0) {
  const $ = _c(16);
  const {
    segments,
    size,
    bathrooms
  } = t0;
  if (!segments.length && !size && !bathrooms) {
    return null;
  }
  let t1;
  let t2;
  let t3;
  if ($[0] !== segments || $[1] !== size) {
    const groupedSegments = groupBy(segments, _temp);
    const singleSegment = segments.length === 1;
    t1 = "flex flex-col gap-4";
    if ($[5] !== size) {
      t2 = size && <RoomSegment roomType="roomSize">{`Größe: ${size} m²`}</RoomSegment>;
      $[5] = size;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    let t4;
    if ($[7] !== singleSegment) {
      t4 = roomTypeSegments => roomTypeSegments.map((segment_0, index) => <RoomSegment key={index} roomType={segment_0.roomType}>{!singleSegment && <SegmentTitle roomType={segment_0.roomType} suffix={roomTypeSegments.length > 1 ? index + 1 : undefined} />}<SegmentBeds beds={segment_0.beds} /></RoomSegment>);
      $[7] = singleSegment;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    t3 = Object.values(groupedSegments).map(t4);
    $[0] = segments;
    $[1] = size;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[9] !== bathrooms) {
    t4 = bathrooms !== 0 && <RoomSegment roomType="bathroom"><SegmentTitle roomType="bathroom" suffix={bathrooms} /></RoomSegment>;
    $[9] = bathrooms;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t1 || $[12] !== t2 || $[13] !== t3 || $[14] !== t4) {
    t5 = <ul className={t1}>{t2}{t3}{t4}</ul>;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
}
function _temp(segment) {
  return segment.roomType;
}
type RoomSegmentProps = PropsWithChildren<{
  roomType: RoomType;
}>;
function RoomSegment(t0) {
  const $ = _c(7);
  const {
    roomType,
    children
  } = t0;
  const Icon = SEGMENT_ICONS[roomType] ?? Fragment;
  let t1;
  if ($[0] !== Icon) {
    t1 = <Icon size={24} />;
    $[0] = Icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = <span>{children}</span>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <li className="flex items-center gap-6">{t1}{t2}</li>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
type SegmentBedsProps = {
  beds: AccommodationRoomSegmentBed[];
};
const bedNames: Record<string, readonly [string, string]> = {
  bigDoubleBed: ['Großes Doppelbett', 'Große Doppelbetten'],
  bunkBed: ['Bunkbett', 'Bunkbetten'],
  crib: ['Kinderbett', 'Kinderbetten'],
  doubleBed: ['Doppelbett', 'Doppelbetten'],
  frenchDoubleBed: ['Französisches Doppelbett', 'Französische Doppelbetten'],
  futon: ['Futon', 'Futons'],
  kingBed: ['Kingsize-Bett', 'Kingsize-Betten'],
  murphyBed: ['Schrankbett', 'Schrankbetten'],
  queenBed: ['Queensize-Bett', 'Queensize-Betten'],
  rollawayBed: ['Zustellbett', 'Zustellbetten'],
  singleBed: ['Einzelbett', 'Einzelbetten'],
  sofaBed: ['Sofabett', 'Sofabetten'],
  tatamiMat: ['Tatami-Matte', 'Tatami-Matten'],
  twinBed: ['Einzelbett', 'Einzelbetten'],
  waterBed: ['Wasserbett', 'Wasserbetten']
};
function SegmentBeds(t0) {
  const $ = _c(4);
  const {
    beds
  } = t0;
  if (!beds.length) {
    return null;
  }
  let t1;
  if ($[0] !== beds) {
    const bedDescriptions = beds.map(_temp2);
    t1 = bedDescriptions.join(", ");
    $[0] = beds;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <span>{t1}</span>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp2(bed) {
  return `${formatGermanNumber(bed.count)} ${bedNames[bed.bedType]?.[bed.count === 1 ? 0 : 1] ?? bed.bedType}`;
}
type SegmentTitleProps = {
  roomType: Exclude<RoomType, 'roomSize'>;
  suffix?: number | null;
};
function SegmentTitle(t0) {
  const $ = _c(2);
  const {
    roomType,
    suffix
  } = t0;
  let title = "Wohnzimmer: ";
  if (roomType === "bedroom") {
    title = `Schlafzimmer ${suffix ?? ""}: `;
  } else {
    if (roomType === "bathroom") {
      title = `Badezimmer: ${suffix ?? ""}`;
    }
  }
  let t1;
  if ($[0] !== title) {
    t1 = <span>{title}</span>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}